import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react';
import { FormControlLabel, Modal, Radio, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblCase, reviewPblCaseByOc, uploadPblFile } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { createPblUploadFormData, getUploadedPblFileId } from '@/components/Pbl/VolunteerRegistration/uploadedDocument';
import { Button } from '@/components/UI/Button';
import { SignatureModal } from '@/components/UI/Modal';
import { Text } from '@/components/UI/Typography/Text';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import { getPblAssistanceProfileId, isPblAssistanceActive } from '@/utils/pblAssistance';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import {
  areAllChecklistItemsYes,
  buildOcCaseReviewPayload,
  getCaseInitiatedRedirectPath,
  getPblCaseDisplayId,
  hasAnyChecklistItemNo,
  hasUnansweredChecklistItem,
  initialOcCaseReviewForm,
  mapPblCaseToOcPendingReviewCase,
  signOcCaseReview,
} from './caseReviewState';
import {
  ActionRow,
  CaseInfoAction,
  ChecklistIntro,
  DateText,
  DetailPanel,
  InfoGrid,
  InfoLabel,
  InfoValue,
  ModalAction,
  ModalSurface,
  ModalText,
  ModalTitle,
  QuestionBlock,
  QuestionPrompt,
  QuestionTitle,
  RadioColumn,
  RadioLabelText,
  SectionTitle,
  SignatureBlock,
  SignaturePreview,
  SignaturePreviewAction,
  SignaturePreviewImage,
  SignatureRow,
  SignedBadge,
  TitleBar,
} from './styles';
import { CaseInformationModal } from './CaseInformationModal';
import type { PblOcCaseReviewForm, PblOcChecklistAnswer, PblOcChecklistKey, PblOcPendingReviewCase } from './types';
import type { PblCase } from '@/types/pbl';
import { useTranslation } from 'react-i18next';

const ocReviewKey = 'pbl.citizenAuthorizationCaseCreate.ocReview';
const ocReviewChecklistKey = 'pbl.citizenAuthorizationCaseCreate.ocReview.checklist';

const getChecklistQuestions = (
  t: (key: string) => string
): Array<{ key: PblOcChecklistKey; title: string; prompt: string }> => [
  {
    key: 'sub_account_eform_completed',
    title: t(`${ocReviewChecklistKey}.questions.subAccountEformCompleted.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.subAccountEformCompleted.prompt`),
  },
  {
    key: 'identity_verified',
    title: t(`${ocReviewChecklistKey}.questions.identityVerified.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.identityVerified.prompt`),
  },
  {
    key: 'service_scope_confirmed',
    title: t(`${ocReviewChecklistKey}.questions.serviceScopeConfirmed.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.serviceScopeConfirmed.prompt`),
  },
  {
    key: 'scheme_rules_explained',
    title: t(`${ocReviewChecklistKey}.questions.schemeRulesExplained.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.schemeRulesExplained.prompt`),
  },
  {
    key: 'online_enquiry_ready',
    title: t(`${ocReviewChecklistKey}.questions.onlineEnquiryReady.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.onlineEnquiryReady.prompt`),
  },
  {
    key: 'declarations_completed',
    title: t(`${ocReviewChecklistKey}.questions.declarationsCompleted.title`),
    prompt: t(`${ocReviewChecklistKey}.questions.declarationsCompleted.prompt`),
  },
];

const dataUrlToFile = (dataUrl: string, filename: string) => {
  const [metadata, base64Data] = dataUrl.split(',');
  const mimeType = metadata.match(/data:(.*);base64/)?.[1] || 'image/png';
  const binary = window.atob(base64Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new File([bytes], filename, { type: mimeType });
};

const PblOcManageCaseDetail = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const tr = (key: string) => t(key as any) as string;
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { isMobile, isTablet } = useResize();
  const [item, setItem] = useState<PblOcPendingReviewCase | null>(null);
  const [reviewedCase, setReviewedCase] = useState<PblCase | null>(null);
  const checklistQuestions = useMemo(() => getChecklistQuestions(t), [t]);
  const [form, setForm] = useState<PblOcCaseReviewForm>(initialOcCaseReviewForm);
  const [infoOpen, setInfoOpen] = useState(false);
  const [proceedOpen, setProceedOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [incompleteOpen, setIncompleteOpen] = useState(false);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const numericCaseId = Number(caseId);

    const loadCase = async () => {
      if (!Number.isFinite(numericCaseId)) {
        errorHandler(t('pbl.pagesHardcoded.ocManageCases.detail.invalidCaseId'));
        return;
      }

      try {
        const response = await getPblCase(numericCaseId);

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.detail.failedToLoadCase'));
          return;
        }

        if (isMounted) {
          if (response.data.data.status !== 'pending_oc_review') {
            navigate(getCaseInitiatedRedirectPath(response.data.data.id), { replace: true });
            return;
          }

          setItem(mapPblCaseToOcPendingReviewCase(response.data.data));
        }
      } catch (error: any) {
        if (isMounted) {
          errorHandler(
            error.response?.data?.message ||
              error.response?.data?.data ||
              t('pbl.pagesHardcoded.ocManageCases.detail.failedToLoadCase')
          );
        }
      }
    };

    loadCase();

    return () => {
      isMounted = false;
    };
  }, [caseId]);

  const updateChecklistAnswer = (key: PblOcChecklistKey, value: PblOcChecklistAnswer) => {
    setForm((prev) => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [key]: value,
      },
    }));
  };

  const updateRemarks = (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, additionalRemarks: event.target.value }));
  };

  const closeSignatureModal = () => {
    setIsSignatureModalOpen(false);
  };

  const saveSignature = async (dataUrl: string) => {
    if (!item) return;

    try {
      const file = dataUrlToFile(dataUrl, 'oc-review-staff-signature.png');
      const response = await uploadPblFile(createPblUploadFormData(file, 'oc_review_signature'));
      const fileId = getUploadedPblFileId(response.data.data);

      if (response.data.success === false || typeof fileId !== 'number') {
        throw new Error(response.data.message || 'Failed to upload signature.');
      }

      setForm((prev) =>
        signOcCaseReview(prev, item.assistingStaffName, {
          dataUrl,
          fileId,
          signedAt: new Date(),
        })
      );
      closeSignatureModal();
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.message ||
          t('pbl.pagesHardcoded.ocManageCases.detail.failedToUploadSignature')
      );
      throw error;
    }
  };

  const submitReview = async () => {
    if (!item || isReviewSubmitting) return undefined;

    if (!form.staffSignature || !form.staffSignedAt || typeof form.staffSignatureFileId !== 'number') {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.detail.pleaseSignBeforeSubmittingTheReview'));
      return undefined;
    }

    setIsReviewSubmitting(true);

    try {
      const response = await reviewPblCaseByOc(Number(item.id), buildOcCaseReviewPayload(form));

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.detail.failedToSubmitCaseReview'));
        return undefined;
      }

      setReviewedCase(response.data.data);
      setForm(initialOcCaseReviewForm);
      successHandler(response.data.message || t('pbl.pagesHardcoded.ocManageCases.detail.caseReviewSubmitted'));
      return response.data.data;
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          t('pbl.pagesHardcoded.ocManageCases.detail.failedToSubmitCaseReview')
      );
      return undefined;
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (hasUnansweredChecklistItem(form.checklist)) {
      setIncompleteOpen(true);
      return;
    }

    if (hasAnyChecklistItemNo(form.checklist)) {
      if (!form.additionalRemarks.trim()) {
        errorHandler(tr(`${ocReviewKey}.returnModal.reasonRequired`));
        return;
      }

      if (!form.staffSignature || !form.staffSignedAt || typeof form.staffSignatureFileId !== 'number') {
        errorHandler(t('pbl.pagesHardcoded.ocManageCases.detail.pleaseSignBeforeSubmittingTheReview'));
        return;
      }

      setReturnOpen(true);
      return;
    }

    if (!areAllChecklistItemsYes(form.checklist)) return;

    if (!form.staffSignature || !form.staffSignedAt || typeof form.staffSignatureFileId !== 'number') {
      errorHandler(t('pbl.pagesHardcoded.ocManageCases.detail.pleaseSignBeforeSubmittingTheReview'));
      return;
    }

    setProceedOpen(true);
  };

  const confirmSuccessfulReview = () => {
    if (!item) return;

    setSuccessOpen(false);
    navigate(getCaseInitiatedRedirectPath(reviewedCase?.id ?? item.id), {
      state: { focusMatchingProgress: reviewedCase?.status === 'ai_matching_in_progress' },
    });
  };

  const confirmIncompleteReview = () => {
    setIncompleteOpen(false);
  };

  if (!item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button
              type="button"
              size="md"
              variant="primary"
              rounded={false}
              onClick={() => navigate(PATH.pbl.pendingReviewCases)}
            >
              {t('pbl.pagesHardcoded.ocManageCases.detail.back')}
            </Button>
            <span>
              {t('pbl.pagesHardcoded.ocManageCases.detail.home')}
              <strong>{t('pbl.pagesHardcoded.ocManageCases.detail.proBonoLegalServiceCasesReview')}</strong>
            </span>
          </TitleBar>
        }
      >
        <DetailPanel isTablet={isTablet}>
          <Text margin={0}>{t('pbl.pagesHardcoded.ocManageCases.detail.loadingCase')}</Text>
        </DetailPanel>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button
            type="button"
            size="md"
            variant="primary"
            rounded={false}
            onClick={() => navigate(PATH.pbl.pendingReviewCases)}
          >
            {t('pbl.pagesHardcoded.ocManageCases.detail.back')}
          </Button>
          <span>
            {t('pbl.pagesHardcoded.ocManageCases.detail.home')}
            <strong>{t('pbl.pagesHardcoded.ocManageCases.detail.proBonoLegalServiceCasesReview')}</strong>
          </span>
        </TitleBar>
      }
    >
      <DetailPanel isTablet={isTablet} onSubmit={submit}>
        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.detail.districtOfficeStaffInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.detail.legcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.officeName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.detail.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.assistingStaffName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.ocManageCases.detail.officeContact')}</InfoLabel>
          <InfoValue>{item.officeContact}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.detail.caseInformation')}</SectionTitle>
        <CaseInfoAction>
          <Button type="button" size="lg" variant="primary" rounded={false} onClick={() => setInfoOpen(true)}>
            {t('pbl.pagesHardcoded.ocManageCases.detail.viewInformation')}
          </Button>
        </CaseInfoAction>

        <SectionTitle>{t(`${ocReviewChecklistKey}.title`)}</SectionTitle>
        <ChecklistIntro>{t(`${ocReviewChecklistKey}.intro`)}</ChecklistIntro>

        {checklistQuestions.map((question) => (
          <QuestionBlock key={question.key}>
            <QuestionTitle>{question.title}</QuestionTitle>
            <QuestionPrompt>{question.prompt}</QuestionPrompt>
            <RadioColumn>
              {(['yes', 'no'] as const).map((answer) => (
                <FormControlLabel
                  key={answer}
                  control={
                    <Radio
                      checked={form.checklist[question.key] === answer}
                      onChange={() => updateChecklistAnswer(question.key, answer)}
                    />
                  }
                  label={
                    <RadioLabelText>
                      {answer === 'yes'
                        ? t(`${ocReviewChecklistKey}.answers.yes`)
                        : t(`${ocReviewChecklistKey}.answers.no`)}
                    </RadioLabelText>
                  }
                />
              ))}
            </RadioColumn>
          </QuestionBlock>
        ))}

        <TextField
          label={
            hasAnyChecklistItemNo(form.checklist)
              ? tr(`${ocReviewKey}.returnModal.remarksLabel`)
              : t('pbl.pagesHardcoded.ocManageCases.detail.additionalRemarksOptional')
          }
          helperText={
            hasAnyChecklistItemNo(form.checklist)
              ? tr(`${ocReviewKey}.returnModal.remarksHelp`)
              : t('pbl.pagesHardcoded.ocManageCases.detail.useThisSpaceToRecordAnySpecialCircumstances')
          }
          variant="standard"
          value={form.additionalRemarks}
          onChange={updateRemarks}
          multiline
          fullWidth
        />

        <SignatureBlock>
          <SectionTitle>{t('pbl.pagesHardcoded.ocManageCases.detail.signature')}</SectionTitle>
          <SignatureRow isMobile={isMobile}>
            <Text as="span" margin={0}>
              {t('pbl.pagesHardcoded.ocManageCases.detail.signatureOfStaffLegcoMembersOffice')}
            </Text>
            {form.staffSignature ? (
              <SignedBadge>{t('pbl.pagesHardcoded.ocManageCases.detail.signed')}</SignedBadge>
            ) : (
              <Button
                type="button"
                size="md"
                variant="primary"
                rounded={false}
                onClick={() => setIsSignatureModalOpen(true)}
              >
                {t('pbl.pagesHardcoded.ocManageCases.detail.sign')}
              </Button>
            )}
            <DateText>
              {t('pbl.pagesHardcoded.ocManageCases.detail.date')}
              {form.staffSignedAt ? formatPblDisplayDate(form.staffSignedAt) : ''}
            </DateText>
          </SignatureRow>
          {form.staffSignaturePreview ? (
            <SignaturePreview>
              <SignaturePreviewImage
                src={form.staffSignaturePreview}
                alt={t('pbl.pagesHardcoded.ocManageCases.detail.ocStaffSignaturePreview')}
              />
              <SignaturePreviewAction type="button" onClick={() => setIsSignatureModalOpen(true)}>
                {t('pbl.pagesHardcoded.ocManageCases.detail.reSign')}
              </SignaturePreviewAction>
            </SignaturePreview>
          ) : null}
        </SignatureBlock>

        <ActionRow isMobile={isMobile}>
          <Button type="submit" size="lg" variant="primary" rounded={false}>
            {hasAnyChecklistItemNo(form.checklist)
              ? tr(`${ocReviewKey}.returnModal.action`)
              : t('pbl.pagesHardcoded.ocManageCases.detail.submit')}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="primary-invert"
            rounded={false}
            onClick={() => navigate(PATH.pbl.pendingReviewCases)}
          >
            {t('pbl.pagesHardcoded.ocManageCases.detail.back')}
          </Button>
        </ActionRow>
      </DetailPanel>

      <CaseInformationModal item={item} open={infoOpen} onClose={() => setInfoOpen(false)} />

      <SignatureModal
        isOpen={isSignatureModalOpen}
        onClose={closeSignatureModal}
        onConfirm={saveSignature}
        onEmptySignature={() =>
          errorHandler(t('pbl.pagesHardcoded.ocManageCases.detail.pleaseProvideASignatureBeforeConfirming'))
        }
        title={t('pbl.pagesHardcoded.ocManageCases.detail.signatureOfStaffLegcoMembersOffice2')}
      />

      <Modal open={proceedOpen} onClose={() => setProceedOpen(false)} aria-labelledby="oc-proceed-esign-title">
        <ModalSurface>
          <ModalTitle id="oc-proceed-esign-title">{t(`${ocReviewKey}.proceedModal.title`)}</ModalTitle>
          <ModalText>{t(`${ocReviewKey}.proceedModal.confirmation`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.proceedModal.uponSigning`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.proceedModal.caseIdAssigned`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.proceedModal.consultationArranged`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.proceedModal.accuracyReminder`)}</ModalText>
          <ModalAction>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isReviewSubmitting}
              onClick={async () => {
                const result = await submitReview();
                if (!result) return;
                setProceedOpen(false);
                setSuccessOpen(true);
              }}
            >
              {isReviewSubmitting
                ? t('pbl.pagesHardcoded.ocManageCases.detail.submitting')
                : t(`${ocReviewKey}.proceedModal.proceedToSubmit`)}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isReviewSubmitting}
              onClick={async () => {
                setProceedOpen(false);
              }}
            >
              {t('pbl.pagesHardcoded.ocManageCases.detail.back')}
            </Button>
          </ModalAction>
        </ModalSurface>
      </Modal>

      <Modal open={returnOpen} onClose={() => setReturnOpen(false)} aria-labelledby="oc-return-case-title">
        <ModalSurface>
          <ModalTitle id="oc-return-case-title">{tr(`${ocReviewKey}.returnModal.title`)}</ModalTitle>
          <ModalText>{tr(`${ocReviewKey}.returnModal.confirmation`)}</ModalText>
          <ModalText>{tr(`${ocReviewKey}.returnModal.reasonNotice`)}</ModalText>
          <ModalAction>
            <Button
              type="button"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isReviewSubmitting}
              onClick={async () => {
                const result = await submitReview();
                if (!result) return;
                setReturnOpen(false);
                const assistanceProfileId = getPblAssistanceProfileId();
                const isAssistingThisCitizen =
                  isPblAssistanceActive() &&
                  (!item.citizenProfileId || assistanceProfileId === item.citizenProfileId);

                navigate(
                  isAssistingThisCitizen
                    ? PATH.pbl.citizenCaseEdit.replace(':caseId', String(result.id))
                    : PATH.pbl.pendingReviewCases
                );
              }}
            >
              {isReviewSubmitting
                ? t('pbl.pagesHardcoded.ocManageCases.detail.submitting')
                : tr(`${ocReviewKey}.returnModal.action`)}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              disabled={isReviewSubmitting}
              onClick={() => setReturnOpen(false)}
            >
              {t('pbl.pagesHardcoded.ocManageCases.detail.back')}
            </Button>
          </ModalAction>
        </ModalSurface>
      </Modal>

      <Modal open={successOpen} onClose={confirmSuccessfulReview} aria-labelledby="oc-case-success-title">
        <ModalSurface>
          <ModalTitle id="oc-case-success-title">
            {t('pbl.pagesHardcoded.ocManageCases.detail.caseInitiatedSuccessfully')}
          </ModalTitle>
          <ModalText>
            {t('pbl.pagesHardcoded.ocManageCases.detail.theCaseHasBeenSuccessfullyInitiatedFollowingCompletion')}
            <br />
            {t('pbl.pagesHardcoded.ocManageCases.detail.caseId')}
            <strong>{reviewedCase ? getPblCaseDisplayId(reviewedCase) : item.caseId}</strong>
          </ModalText>
          <ModalText>{t('pbl.pagesHardcoded.ocManageCases.detail.thisConfirmsThat')}</ModalText>
          <ModalText>{t('pbl.pagesHardcoded.ocManageCases.detail.theEnquirerSSubmissionHasBeenAcceptedAnd')}</ModalText>
          <ModalText>{t('pbl.pagesHardcoded.ocManageCases.detail.theCopyOfAllEFormsRelatedTo')}</ModalText>
          {reviewedCase?.status === 'ai_matching_in_progress' && (
            <ModalText>{t('pbl.pagesHardcoded.ocManageCases.detail.matchingStartedGuidance')}</ModalText>
          )}
          <ModalAction>
            <Button type="button" size="lg" variant="primary" rounded={false} onClick={confirmSuccessfulReview}>
              {reviewedCase?.status === 'ai_matching_in_progress'
                ? t('pbl.pagesHardcoded.ocManageCases.detail.viewMatchingProgress')
                : t('pbl.pagesHardcoded.ocManageCases.detail.confirm')}
            </Button>
          </ModalAction>
        </ModalSurface>
      </Modal>

      <Modal open={incompleteOpen} onClose={confirmIncompleteReview} aria-labelledby="oc-checklist-incomplete-title">
        <ModalSurface>
          <ModalTitle id="oc-checklist-incomplete-title">{t(`${ocReviewKey}.incompleteModal.title`)}</ModalTitle>
          <ModalText>{t(`${ocReviewKey}.incompleteModal.cannotProceed`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.incompleteModal.reviewChecklist`)}</ModalText>
          <ModalText>{t(`${ocReviewKey}.incompleteModal.noCaseInitiated`)}</ModalText>
          <ModalAction>
            <Button type="button" size="lg" variant="primary" rounded={false} onClick={confirmIncompleteReview}>
              {reviewedCase?.status === 'oc_review_failed'
                ? t('pbl.pagesHardcoded.ocManageCases.detail.confirm')
                : t(`${ocReviewKey}.incompleteModal.reviewChecklistAction`)}
            </Button>
          </ModalAction>
        </ModalSurface>
      </Modal>
    </PageContainer>
  );
};

export default PblOcManageCaseDetail;
